/**
 * ROOM PRESENCE — "<user> typing…" (MENTIONS.md §6). Ephemeral, never stored: the browser publishes straight to
 * the room's PRESENCE channel ``/default/presence/{scope}/{networkId}/{poolId}`` (its own channel — the run stream
 * stays server-written). The authorizer applies the room's membership rules and the namespace ``onPublish`` stamps
 * WHO from the verified token, so nobody can type as someone else.
 *  • send side (Slack-style): TYPING at most every ``TYPING_EVERY_MS`` while keys land; STOPPED on send / blur /
 *    ``IDLE_MS`` without a key.
 *  • receive side: a typer silent for ``EXPIRE_MS`` drops off (a closed tab never sticks); me is never listed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeAppSyncEvents, publishAppSyncEvent, appsyncEventEndpoints } from '../services/appsyncEvents';

export const TYPING_EVERY_MS = 3000;
export const IDLE_MS = 5000;
export const EXPIRE_MS = 6000;
export const presenceChannel = (scope, networkId, poolId) =>
  `/default/presence/${scope}/${networkId}/${(poolId || '').trim() || 'default'}`;

/** "Pani is typing…" · "Pani and Raj are typing…" · "Several people are typing…" ('' when nobody). */
export function typingLine(names) {
  const n = (names || []).filter(Boolean);
  if (!n.length) return '';
  if (n.length === 1) return `${n[0]} is typing…`;
  if (n.length === 2) return `${n[0]} and ${n[1]} are typing…`;
  return 'Several people are typing…';
}

const parse = (e) => {
  if (typeof e === 'string') { try { return JSON.parse(e); } catch { return null; } }
  return e && typeof e === 'object' ? e : null;
};

/** → { typers: [{sub, email}], onTyping(), onStopped() } */
export default function useRoomTyping({ networkId, scope, poolId, meSub, endpoints = null, now = () => Date.now() }) {
  const [typers, setTypers] = useState({});
  const { httpDns, realtimeDns } = endpoints || appsyncEventEndpoints();
  const channel = networkId && scope ? presenceChannel(scope, networkId, poolId) : '';
  const lastSent = useRef(0);
  const idleTimer = useRef(null);
  const meRef = useRef(meSub);
  meRef.current = meSub;

  useEffect(() => {
    setTypers({});
    if (!channel || !httpDns || !realtimeDns) return undefined;
    const unsub = subscribeAppSyncEvents(httpDns, realtimeDns, channel, {
      onData: (list) => {
        (list || []).map(parse).filter(Boolean).forEach((evt) => {
          const p = evt.payload && typeof evt.payload === 'object' ? evt.payload : evt;
          const sub = String(p.sub || '').trim();
          if (!sub || sub === meRef.current) return;
          setTypers((prev) => {
            const next = { ...prev };
            if (p.type === 'STOPPED') delete next[sub];
            else if (p.type === 'TYPING') next[sub] = { sub, email: p.email || '', at: now() };
            return next;
          });
        });
      },
    });
    const sweep = setInterval(() => {
      setTypers((prev) => {
        const t = now(); const keep = Object.values(prev).filter((x) => t - x.at < EXPIRE_MS);
        return keep.length === Object.keys(prev).length ? prev : Object.fromEntries(keep.map((x) => [x.sub, x]));
      });
    }, 1000);
    return () => { unsub(); clearInterval(sweep); };
  }, [channel, httpDns, realtimeDns]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback((type) => {
    if (!channel || !httpDns) return;
    publishAppSyncEvent(httpDns, channel.replace(/^\//, ''), { type }).catch(() => {});
  }, [channel, httpDns]);

  const onStopped = useCallback(() => {
    if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
    if (!lastSent.current) return;               // never said I was typing → nothing to retract
    lastSent.current = 0;
    send('STOPPED');
  }, [send]);

  const onTyping = useCallback(() => {
    const t = now();
    if (t - lastSent.current >= TYPING_EVERY_MS) { lastSent.current = t; send('TYPING'); }
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(onStopped, IDLE_MS);
  }, [send, onStopped]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { onStopped(); }, [onStopped]);   // leaving the room retracts it

  return { typers: Object.values(typers), onTyping, onStopped };
}
